// GracePeriodBanner — fires when a paying creator's platform fee fails
// and the grace window starts ticking. Visual urgency increases
// as the deadline approaches. Single one-tap "Pay now" button.
//
// Sprint Feb 9, 2026 — closes a half-built feature: backend webhook
// already auto-suspends users on day N, but there was no banner
// warning them they're in the danger zone. Real safety hole now closed.
import { useEffect, useState } from "react";
import { Warning, CreditCard, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FALLBACK_GRACE_DAYS = 14;

function daysSince(iso) {
  if (!iso) return 0;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.floor(ms / (1000 * 60 * 60 * 24));
  } catch { return 0; }
}

export default function GracePeriodBanner({ user }) {
  const [dismissed, setDismissed] = useState(false);
  const [paying, setPaying] = useState(false);
  const [graceDays, setGraceDays] = useState(FALLBACK_GRACE_DAYS);

  // Read the live super-admin-tunable grace window so the countdown
  // matches whatever the backend will actually enforce.
  useEffect(() => {
    if (!user?.grace_period_started_at) return;
    let cancel = false;
    (async () => {
      try {
        const r = await api.get("/stripe/config");
        const d = parseInt(r.data?.grace_period_days, 10);
        if (!cancel && Number.isFinite(d) && d > 0) setGraceDays(d);
      } catch { /* keep fallback */ }
    })();
    return () => { cancel = true; };
  }, [user?.grace_period_started_at]);

  // Hide for users not in grace.
  if (!user?.grace_period_started_at) return null;
  if (dismissed) return null;

  const daysIn = daysSince(user.grace_period_started_at);
  const daysLeft = Math.max(0, graceDays - daysIn);

  // Severity tiers — color + tone shift as the deadline approaches
  let severity = "warn";
  if (daysLeft <= 3) severity = "critical";
  else if (daysLeft <= 7) severity = "urgent";

  const tone = {
    warn: { bg: "bg-[#FDF1ED] border-[#E5BBA8]", text: "text-[#7A3A2A]", btn: "bg-[#C16E5A] hover:bg-[#A85742]" },
    urgent: { bg: "bg-[#FBE9E1] border-[#C16E5A]", text: "text-[#7A3A2A]", btn: "bg-[#C16E5A] hover:bg-[#A85742]" },
    critical: { bg: "bg-[#7A3A2A] border-[#7A3A2A]", text: "text-[#FDFCF8]", btn: "bg-[#FDFCF8] text-[#7A3A2A] hover:bg-white" },
  }[severity];

  const message = daysLeft === 0
    ? "Your platform fee failed and the grace period ended today. Your silos will go read-only at any moment."
    : daysLeft === 1
    ? "Your platform fee failed. You have 1 day before your silos go read-only."
    : `Your platform fee failed. You have ${daysLeft} days before your silos go read-only.`;

  const payNow = async () => {
    setPaying(true);
    try {
      // Past-due users have an existing Stripe customer + subscription —
      // the right move is to send them to the billing portal where they
      // can update their card and Stripe will auto-retry the failed
      // invoice. (A fresh /checkout would create a duplicate sub.)
      const r = await api.post("/stripe/platform/portal", { origin_url: window.location.origin });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error("Couldn't open billing portal. Try again.");
        setPaying(false);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't open billing portal.");
      setPaying(false);
    }
  };

  return (
    <div
      data-testid="grace-period-banner"
      data-severity={severity}
      className={`${tone.bg} ${tone.text} border-l-4 px-4 py-3 mb-4 flex items-start gap-3 rounded-md`}
    >
      <Warning size={20} weight="duotone" className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium leading-snug">{message}</div>
        <div className="text-[11px] opacity-80 mt-0.5">
          One tap to fix · we keep your fans, your silos, and your earnings as if nothing happened.
        </div>
      </div>
      <button
        onClick={payNow}
        disabled={paying}
        data-testid="grace-banner-pay-now"
        className={`shrink-0 ${tone.btn} text-xs font-medium px-4 py-2 rounded-md flex items-center gap-1.5 transition-colors disabled:opacity-60`}
      >
        <CreditCard size={13} weight="bold" />
        {paying ? "Opening…" : "Pay now"}
      </button>
      {severity !== "critical" && (
        <button
          onClick={() => setDismissed(true)}
          data-testid="grace-banner-dismiss"
          className="shrink-0 opacity-50 hover:opacity-100 transition-opacity"
          aria-label="Dismiss for this session"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
}
